import type { FormEventHandler } from 'react'
import React, { useCallback } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import Image from 'components/Common/Image'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import PaneBody from 'components/Common/Pane/PaneBody'
import PaneHeader from 'components/Common/Pane/PaneHeader'
import partnershipUserFields from 'lib/partnership/partnershipUserFields'
import TextInput from 'components/Common/Form/Input/TextInput'
import { getPartnershipFields } from 'selectors/partnershipSelectors'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { linkPartnershipAccount } from 'actions/AuthActions'
import TextButton from '../../../../LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'

interface Props {
  partnership: App.Partnership
  error?: any
  partnershipFields: Record<string, App.Partnership>
  dismissable?: boolean
}

function AccountAccessPartnershipLinkAccount(props: Props) {
  const {
    error,
    partnership,
    partnershipFields,
    dismissable,
  } = props

  const intl = useIntl()

  const {
    partnershipAccountNumberField,
    otherPartnershipUserFields,
  } = partnershipUserFields(partnership, partnershipFields)

  const hasPartnership = useAppSelector(state => !!state.auth.account.partnerships[partnership.prefix]?.account_id)

  const dispatch = useAppDispatch()

  const onSubmit = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    const partnershipData = formToObject<any>(event.currentTarget)
    dispatch(linkPartnershipAccount(partnership.prefix, partnershipData))
  }, [dispatch, partnership])

  const linkedTitle = intl.formatMessage({
    id: 'uEYd7-dEcYdT7lzTnwo2',
    defaultMessage: 'Your account has been linked',
    description: 'Modal title after partnership account has been successfully linked',
  })

  const logoAlt = intl.formatMessage({
    id: 'uydvEnLheWpaNsgavoZZ',
    defaultMessage: '{programName} logo',
    description: 'Accessibility alt text for partnership program logo image',
  }, { programName: partnership.programName })

  // linkDescription uses FormattedMessage inline (rich text with titlecase BodyText)

  const saveLabel = intl.formatMessage({
    id: 'FJM75FKGgbaQ0Wp00n2X',
    defaultMessage: 'Save',
    description: 'Submit button text for partnership account linking form',
  })

  return (
    <>
      <ModalHeader title={hasPartnership ? linkedTitle : ''} dismissible={dismissable} />
      <ModalBody>
        <ModalContent>
          {!hasPartnership && <>
            <PaneHeader size="lg-x">
              <BodyText variant="large" align="center">
                <Image
                  highRes
                  id={partnership.programLogo.id}
                  width={172}
                  alt={logoAlt}
                />
              </BodyText>
              <br />
              <BodyText variant="large" align="center">
                <FormattedMessage
                  id="D_Q35ND1elRwVVpOrX7t"
                  defaultMessage="Link your <titlecase>{programName}</titlecase> account to earn {rewardName} on eligible holidays"
                  description="Description text encouraging user to link their partnership account"
                  values={{
                    programName: partnership.programName,
                    rewardName: partnership.rewardName,
                    titlecase: (chunks) => <BodyText variant="large" as="span" format="titlecase">{chunks}</BodyText>,
                  }}
                />
              </BodyText>
            </PaneHeader>
            <PaneBody size="xl">
              <form onSubmit={onSubmit} data-testid="link-partnership-form">
                <VerticalSpacer gap={24}>
                  {error?.message && <MessageBanner kind="critical">{error.message}</MessageBanner>}
                  {partnershipAccountNumberField && <TextInput
                    aria-label={partnershipAccountNumberField.label}
                    maxLength={partnership.numberMaxLength}
                    name={partnershipAccountNumberField.name}
                    id={partnershipAccountNumberField.name}
                    key={partnershipAccountNumberField.name}
                    defaultValue={partnershipAccountNumberField.value}
                    type="number"
                    required={partnershipAccountNumberField.required}
                    placeholder={partnershipAccountNumberField.label}
                  />}
                  {otherPartnershipUserFields?.map(field => (
                    <TextInput
                      aria-label={field.label}
                      key={field.name}
                      id={field.name}
                      required={field.required}
                      type="text"
                      name={field.name}
                      defaultValue={field.value}
                      placeholder={field.label}
                    />
                  ))}
                  <TextButton
                    kind="primary"
                    type="submit"
                    fit="flex"
                  >
                    {saveLabel}
                  </TextButton>
                </VerticalSpacer>
              </form>
            </PaneBody>
          </>}
        </ModalContent>
      </ModalBody>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    partnership: state.geo.partnership,
    partnershipFields: getPartnershipFields(state),
  }
}

export default connect(mapStateToProps)(AccountAccessPartnershipLinkAccount)
